import { useMemo, useState } from 'react';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { embryoStageColor, embryoStageLabel } from '../../../core/constants/transfers';
import { useAvailableEmbryos } from '../../embryos/contexts/embryos';

// Modal para sumar embriones del banco (congelados disponibles) a la TE. Buscador
// avanzado por DONADORA y por TORO (sobre el linaje de cada embrión). El contrato
// de embryos/available filtra server-side por `donor` (id) pero NO por `sire`, y la
// donadora acá se busca por texto (nombre/número), no por id — así que el filtrado
// de ambos campos se hace client-side sobre el banco cargado (limit 500, el tope
// documentado). Si el banco supera ese tope (pagination.total > cargados) se avisa,
// para que un "sin coincidencias" no se confunda con un embrión fuera de la ventana.
// Excluye los que ya están en la grilla; devuelve los elegidos vía onAdd.

// Etiqueta de toro: nombre y/o registro (ambos son buscables → ambos visibles).
function sireLabel(s) {
    if (!s) return '';
    return [s.name, s.register].filter(Boolean).join(' · ');
}

export default function BankPicker({ existingIds, onAdd, onClose }) {
    const cardRef = useModalDialog(onClose);
    const { data, isLoading, error } = useAvailableEmbryos({ source: 'frozen', limit: 500 });
    const all = useMemo(() => (data?.items ?? []).filter((e) => !existingIds.has(e.id)), [data, existingIds]);

    // El banco puede exceder el tope de página: si total > cargados, la búsqueda
    // client-side opera sobre una ventana incompleta y hay que avisarlo.
    const total = data?.pagination?.total ?? null;
    const loaded = data?.items?.length ?? 0;
    const truncated = total != null && total > loaded;

    const [donorQ, setDonorQ] = useState('');
    const [sireQ, setSireQ] = useState('');
    const [picked, setPicked] = useState(() => new Set());

    const available = useMemo(() => {
        const dq = donorQ.trim().toLowerCase();
        const sq = sireQ.trim().toLowerCase();
        const has = (v, q) => (v || '').toLowerCase().includes(q);
        return all.filter((e) => {
            const d = e.lineage?.donor;
            const s = e.lineage?.sire;
            const okDonor = !dq || has(d?.name, dq) || has(d?.number, dq);
            const okSire = !sq || has(s?.name, sq) || has(s?.register, sq);
            return okDonor && okSire;
        });
    }, [all, donorQ, sireQ]);

    const toggle = (id) => setPicked((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    // seleccionar/limpiar todos los visibles (útil para "todos los de esta donadora").
    const allVisiblePicked = available.length > 0 && available.every((e) => picked.has(e.id));
    const toggleAllVisible = () => setPicked((prev) => {
        const next = new Set(prev);
        if (allVisiblePicked) available.forEach((e) => next.delete(e.id));
        else available.forEach((e) => next.add(e.id));
        return next;
    });

    const confirm = () => {
        // agrega TODOS los marcados (aunque el filtro actual los oculte), nunca pierde selección.
        onAdd(all.filter((e) => picked.has(e.id)));
        onClose();
    };

    const filtering = !!(donorQ.trim() || sireQ.trim());

    return (
        <div className="transfer-pop" role="dialog" aria-modal="true" aria-label="Embriones del banco">
            <div className="transfer-pop__overlay" onClick={onClose} aria-hidden="true" />
            <div className="transfer-pop__card transfer-pop__card--wide" ref={cardRef}>
                <header className="transfer-pop__head">
                    <h2 className="transfer-pop__title">Embriones del banco (congelados)</h2>
                    <button type="button" className="transfer-pop__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="transfer-bank__search">
                    <label className="transfer-bank__field">
                        <span className="transfer-bank__field-label">Donadora</span>
                        <input type="text" value={donorQ} onChange={(e) => setDonorQ(e.target.value)} placeholder="Nombre o número…" />
                    </label>
                    <label className="transfer-bank__field">
                        <span className="transfer-bank__field-label">Toro</span>
                        <input type="text" value={sireQ} onChange={(e) => setSireQ(e.target.value)} placeholder="Nombre o registro…" />
                    </label>
                </div>

                {truncated && (
                    <p className="transfer-bank__cap" role="note">
                        El banco tiene {total} embriones; se cargaron los primeros {loaded}. Un embrión fuera de ese rango podría no aparecer en la búsqueda.
                    </p>
                )}

                {error ? (
                    <p className="transfer-empty" role="status">No se pudieron cargar los embriones del banco.</p>
                ) : isLoading ? (
                    <p className="transfer-empty" role="status">Cargando banco…</p>
                ) : !all.length ? (
                    <p className="transfer-empty" role="status">No hay embriones congelados disponibles para agregar.</p>
                ) : !available.length ? (
                    <p className="transfer-empty" role="status">Ningún embrión coincide con la búsqueda.</p>
                ) : (
                    <>
                        <div className="transfer-bank__toolbar">
                            <span className="transfer-bank__count" aria-live="polite">{available.length} embrión(es){filtering ? ' (filtrados)' : ''}{picked.size ? ` · ${picked.size} marcados` : ''}</span>
                            <button type="button" className="transfer-bank__selall" onClick={toggleAllVisible}>
                                {allVisiblePicked ? 'Quitar visibles' : 'Marcar visibles'}
                            </button>
                        </div>
                        <ul className="transfer-bank">
                            {available.map((e) => {
                                const donor = e.lineage?.donor;
                                const sire = sireLabel(e.lineage?.sire);
                                return (
                                    <li key={e.id} className="transfer-bank__item">
                                        <label className="transfer-bank__check">
                                            <input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} />
                                            <span className="transfer-bank__code">{e.code}</span>
                                            <span className="transfer-stage" style={{ '--stage': embryoStageColor(e.stage) }}>{embryoStageLabel(e.stage)}</span>
                                            <span className="transfer-bank__sub">
                                                {donor ? `${donor.name}${donor.number ? ` · ${donor.number}` : ''}` : ''}
                                                {sire ? `  ·  ${sire}` : ''}
                                            </span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}

                <div className="transfer-pop__actions">
                    <button type="button" className="transfer-pop__cancel" onClick={onClose}>Cancelar</button>
                    <button type="button" className="transfer-pop__save" onClick={confirm} disabled={!picked.size}>
                        Agregar{picked.size ? ` (${picked.size})` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
}
